import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { AlertSeverityEnum } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

const BASE_URL = `${urlBackend}api/v1/gestion-tickets/rotacion`;

export interface IRotacionArea {
  id_rotacion_area: string;
  area_id: string;
  activa: boolean;
  zona_horaria: string;
  notas?: string | null;
  fecha_creacion?: string;
  fecha_modificacion?: string;
}

export interface IRotacionSlot {
  id_slot: string;
  rotacion_area_id: string;
  dia_semana: number;
  hora_inicio: string;
  hora_fin: string;
  usuario_id: number;
  usuario_nombre?: string;
  rol_id: string;
  activo: boolean;
}

export interface IRotacionAreaDetalle {
  rotacion: IRotacionArea;
  slots: IRotacionSlot[];
}

export interface IRotacionSlotPayload {
  dia_semana: number;
  hora_inicio: string;
  hora_fin: string;
  usuario_id: number;
  usuario_nombre?: string;
  rol_id: string;
}

export interface IRotacionTogglePayload {
  activa: boolean;
  zona_horaria?: string;
  notas?: string;
}

const QK_LISTA = ['rotacion', 'lista'];
const QK_AREA = (areaId: string) => ['rotacion', 'area', areaId];

export const useFetchRotaciones = () => {
  return useQuery({
    queryKey: QK_LISTA,
    queryFn: () => get<IRotacionArea[]>(`${BASE_URL}/`, true),
    staleTime: 1000 * 30,
  });
};

export const useFetchRotacionPorArea = (areaId: string | null | undefined) => {
  return useQuery({
    queryKey: QK_AREA(areaId || ''),
    queryFn: () => get<IRotacionAreaDetalle>(`${BASE_URL}/area/${areaId}/`, true),
    enabled: !!areaId,
    staleTime: 1000 * 30,
  });
};

export const useToggleRotacion = () => {
  const setAlert = useUiStore((s) => s.setAlert);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ areaId, payload }: { areaId: string; payload: IRotacionTogglePayload }) =>
      put<IRotacionArea>(`${BASE_URL}/area/${areaId}/toggle/`, payload, true),
    onSuccess: (_d, { areaId }) => {
      qc.refetchQueries({ queryKey: QK_LISTA, type: 'active' });
      qc.refetchQueries({ queryKey: QK_AREA(areaId), type: 'active' });
      setAlert(true, 'Configuración de rotación actualizada', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useCrearSlot = () => {
  const setAlert = useUiStore((s) => s.setAlert);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ areaId, payload }: { areaId: string; payload: IRotacionSlotPayload }) =>
      post<IRotacionSlot>(`${BASE_URL}/area/${areaId}/slots/`, payload, true),
    onSuccess: (_d, { areaId }) => {
      qc.refetchQueries({ queryKey: QK_AREA(areaId), type: 'active' });
      setAlert(true, 'Slot creado', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useActualizarSlot = () => {
  const setAlert = useUiStore((s) => s.setAlert);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      idSlot,
      areaId: _areaId,
      payload,
    }: {
      idSlot: string;
      areaId: string;
      payload: IRotacionSlotPayload;
    }) => put<IRotacionSlot>(`${BASE_URL}/slots/${idSlot}/`, payload, true),
    onSuccess: (_d, { areaId }) => {
      qc.refetchQueries({ queryKey: QK_AREA(areaId), type: 'active' });
      setAlert(true, 'Slot actualizado', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useEliminarSlot = () => {
  const setAlert = useUiStore((s) => s.setAlert);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ idSlot, areaId: _areaId }: { idSlot: string; areaId: string }) =>
      remove(`${BASE_URL}/slots/${idSlot}/`, true),
    onSuccess: (_d, { areaId }) => {
      qc.refetchQueries({ queryKey: QK_AREA(areaId), type: 'active' });
      setAlert(true, 'Slot eliminado', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};
